import { differenceInDays } from 'date-fns';
import { format } from 'date-fns-tz';

/** DateRangePicker の `DateRange<Date>` と同じ形。MUI に依存させないため自前で持つ */
export type DateRangeInput = [Date | null, Date | null];

export type DateRangeChangeResult =
  /** 片方しか選ばれていない。まだ何もしない */
  | { kind: 'ignore' }
  /** 弾く。message をそのまま通知に出す */
  | { kind: 'reject'; message: string }
  /** URL を書き換える */
  | { kind: 'navigate'; startOn: string; endOn: string };

/** `/plans` が許す最大日数（10 日 = diffDays 9） */
const MAX_DIFF_DAYS = 9;

/**
 * 日付範囲の変更を「無視 / 拒否 / 遷移」のどれかに落とす（`/map` のヘッダ用）。
 *
 * Why not カード配車 (ConvenienceDispatchToolbar) を写す: あちらには 10 日制限が
 * 無い。UI は流用するが判定は `V3Plans/presenters/ActionsPresenter.tsx:98-112` を
 * 正とする（ユーザー指示「動作は /plans を参考に」）。
 *
 * Why not コンポーネント内に直書きする: MUI DateRangePicker を DOM 経由で操作する
 * テストはリポジトリに前例が無く脆い。判定だけ純粋関数にすれば、境界値
 * （diffDays 9 と 10）をテストで固定できる。
 */
export function resolveDateRangeChange(range: DateRangeInput): DateRangeChangeResult {
  const [start, end] = range;
  if (!start || !end) return { kind: 'ignore' };

  const diffDays = differenceInDays(end, start);

  // 並びは `/plans` に合わせているが、この 2 つは同時に真にならない
  // （逆転時は diffDays が負なので `> 9` に該当しない）。順序に意味は無い
  if (diffDays < 0) {
    return { kind: 'reject', message: '終了日は開始日よりも過去に設定できません' };
  }
  if (diffDays > MAX_DIFF_DAYS) {
    return { kind: 'reject', message: '最大取得可能日数の10日を超過しています' };
  }

  // 既存 2 画面と同じ形にする。date-fns-tz の format は timeZone を無視する既知バグ
  // （FE #2376）があるが、ここで直すと /plans・カード配車と挙動が割れる
  return {
    kind: 'navigate',
    startOn: format(start, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' }),
    endOn: format(end, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' }),
  };
}
